/**
 * Per-frame registry of constrained world positions for Follow Object modifiers.
 *
 * PrimitiveRenderer writes the constrained position each frame for any object
 * that has active follow constraints. TransformGizmo reads it so the gizmo
 * tracks the visual mesh position (bone world + stored offset) rather than the
 * stored offset alone.
 *
 * Module-level Map — no React subscription needed. Values are always from the
 * most recently completed frame, so consumers in useFrame see exactly one frame
 * of latency at most (in practice zero, since PrimitiveRenderer mounts earlier
 * in the scene tree and its useFrame runs first).
 */

const _positions = new Map<string, [number, number, number]>();

/** Record the constrained world position for the given object. */
export function setConstrainedPosition(
  id: string,
  x: number,
  y: number,
  z: number,
): void {
  const entry = _positions.get(id);
  if (entry) {
    entry[0] = x;
    entry[1] = y;
    entry[2] = z;
  } else {
    _positions.set(id, [x, y, z]);
  }
}

/** Remove the constrained position entry (call when constraints are removed or object unmounts). */
export function clearConstrainedPosition(id: string): void {
  _positions.delete(id);
}

/**
 * Return the constrained world position for the given object ID, or undefined
 * if no active follow constraint is present.
 */
export function getConstrainedPosition(
  id: string,
): [number, number, number] | undefined {
  return _positions.get(id);
}

/**
 * Drop entries whose owning object is no longer alive. Belt-and-braces for
 * the primary unmount-driven cleanup in PrimitiveRenderer: if the renderer
 * is suspended or remounted without an unmount tick — or an id is removed
 * from the store before the component hears about it — the entry would
 * linger otherwise. Driven by the viewport's scene-delete subscription.
 */
export function pruneConstrainedPositions(aliveIds: ReadonlySet<string>): void {
  for (const id of _positions.keys()) {
    if (!aliveIds.has(id)) _positions.delete(id);
  }
}
